import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import store, { RootState } from '../store';

// Reading slice actions
import {
    fetchReadingPassagesRequest,
    fetchReadingPassageByIdRequest,
    fetchPassageQuestionsRequest,
    fetchReadingRecommendationsRequest,
    rateReadingRecommendationRequest,
    fetchUserReadingProfileRequest,
    updateUserReadingProfileRequest,
    setReadingPassageFilters,
    clearReadingPassageFilters,
    setCurrentFeedbackTone,
} from '@slices/reading/readingSlice';

// Reading sessions slice actions
import {
    fetchReadingSessionsRequest,
    fetchReadingSessionByIdRequest,
    startReadingSessionRequest,
    completeReadingSessionRequest,
    uploadReadingAudioRequest,
    fetchSessionAttemptsRequest,
    submitComprehensionRequest,
    fetchSessionComprehensionRequest,
    fetchSessionFeedbackRequest,
    generateSessionFeedbackRequest,
} from '@slices/reading/readingSessionsSlice';

// Reading analytics slice actions
import {
    fetchReadingAnalyticsRequest,
    fetchWeeklyProgressRequest,
    fetchMonthlyProgressRequest,
    fetchSkillBreakdownRequest,
    fetchWaveformDataRequest,
    compareReadingAudioRequest,
    fetchProgressTimelineRequest,
    fetchStreakDataRequest,
    fetchSkillTrendsRequest,
} from '@slices/reading/readingAnalyticsSlice';

import type {
    ReadingPassageFilters,
    StartReadingSessionRequest,
    AudioUploadRequest,
    SubmitComprehensionRequest,
    FeedbackTone,
} from '@/services/reading/types';

// Optimized state selectors to prevent unnecessary re-renders
const selectReadingState = (state: RootState) => state.reading.reading;
const selectSessionsState = (state: RootState) => state.reading.sessions;
const selectAnalyticsState = (state: RootState) => state.reading.analytics;

// More specific selectors for computed values
const selectIsLoadingStates = createSelector(
    (state: RootState) => state.reading.reading.isLoadingPassages,
    (state: RootState) => state.reading.reading.isLoadingPassage,
    (state: RootState) => state.reading.reading.isLoadingQuestions,
    (state: RootState) => state.reading.reading.isLoadingRecommendations,
    (state: RootState) => state.reading.reading.isLoadingProfile,
    (state: RootState) => state.reading.sessions.isLoadingSessions,
    (state: RootState) => state.reading.sessions.isLoadingSession,
    (state: RootState) => state.reading.sessions.isStartingSession,
    (state: RootState) => state.reading.analytics.isLoadingAnalytics,
    (
        isLoadingPassages,
        isLoadingPassage,
        isLoadingQuestions,
        isLoadingRecommendations,
        isLoadingProfile,
        isLoadingSessions,
        isLoadingSession,
        isStartingSession,
        isLoadingAnalytics
    ) => ({
        isLoadingPassages,
        isLoadingPassage,
        isLoadingQuestions,
        isLoadingRecommendations,
        isLoadingProfile,
        isLoadingSessions,
        isLoadingSession,
        isStartingSession,
        isLoadingAnalytics,
    })
);

const selectErrorStates = createSelector(
    (state: RootState) => state.reading.reading.passagesError,
    (state: RootState) => state.reading.reading.passageError,
    (state: RootState) => state.reading.reading.questionsError,
    (state: RootState) => state.reading.reading.recommendationsError,
    (state: RootState) => state.reading.reading.profileError,
    (state: RootState) => state.reading.sessions.sessionsError,
    (state: RootState) => state.reading.sessions.sessionError,
    (state: RootState) => state.reading.sessions.startSessionError,
    (state: RootState) => state.reading.analytics.analyticsError,
    (
        passagesError,
        passageError,
        questionsError,
        recommendationsError,
        profileError,
        sessionsError,
        sessionError,
        startSessionError,
        analyticsError
    ) => ({
        passagesError,
        passageError,
        questionsError,
        recommendationsError,
        profileError,
        sessionsError,
        sessionError,
        startSessionError,
        analyticsError,
    })
);

const selectCurrentSession = (state: RootState) => state.reading.sessions.currentSession;

export const useReading = () => {
    const dispatch = useDispatch();

    // === OPTIMIZED STATE SELECTORS ===
    const readingState = useSelector(selectReadingState);
    const sessionsState = useSelector(selectSessionsState);
    const analyticsState = useSelector(selectAnalyticsState);

    // Specific selectors for computed values
    const loadingStates = useSelector(selectIsLoadingStates);
    const errorStates = useSelector(selectErrorStates);
    const currentSession = useSelector(selectCurrentSession);

    // === READING PASSAGES MANAGEMENT ===
    const fetchReadingPassages = (filters?: ReadingPassageFilters) => {
        dispatch(fetchReadingPassagesRequest(filters));
    };

    const fetchReadingPassageById = (passageId: string) => {
        dispatch(fetchReadingPassageByIdRequest(passageId));
    };

    const fetchPassageQuestions = (passageId: string) => {
        dispatch(fetchPassageQuestionsRequest(passageId));
    };

    const setFilters = (filters: ReadingPassageFilters) => {
        dispatch(setReadingPassageFilters(filters));
    };

    const clearFilters = () => {
        dispatch(clearReadingPassageFilters());
    };

    // === RECOMMENDATIONS MANAGEMENT ===
    const fetchRecommendations = (limit?: number) => {
        dispatch(fetchReadingRecommendationsRequest(limit));
    };

    const rateRecommendation = (recommendationId: string, rating: number) => {
        dispatch(rateReadingRecommendationRequest({ recommendationId, rating }));
    };

    // === USER PROFILE MANAGEMENT ===
    const fetchUserProfile = () => {
        dispatch(fetchUserReadingProfileRequest());
    };

    const updateUserProfile = (updateData: {
        preferred_topics?: string[];
        preferred_passage_types?: string[];
        reading_goals?: any;
    }) => {
        dispatch(updateUserReadingProfileRequest(updateData));
    };

    // === SESSION MANAGEMENT ===
    const fetchReadingSessions = (params?: { page?: number; pageSize?: number }) => {
        dispatch(fetchReadingSessionsRequest(params || {}));
    };

    const fetchReadingSessionById = (sessionId: string) => {
        dispatch(fetchReadingSessionByIdRequest(sessionId));
    };

    const startReadingSession = (request: StartReadingSessionRequest) => {
        dispatch(startReadingSessionRequest(request));
    };

    const completeReadingSession = (sessionId: string, completionData: any): Promise<any> => {
        return new Promise((resolve, reject) => {
            const unsubscribe = store.subscribe(() => {
                const state = store.getState();
                const sessionState = state.reading.sessions;

                // Check if the operation completed
                if (!sessionState.isCompletingSession) {
                    unsubscribe();
                    if (sessionState.completeSessionError) {
                        reject(new Error(sessionState.completeSessionError));
                    } else {
                        resolve(sessionState.currentSession);
                    }
                }
            });

            // Dispatch the action
            dispatch(completeReadingSessionRequest({ sessionId, completionData }));
        });
    };

    // === AUDIO MANAGEMENT ===
    const uploadAudio = (request: AudioUploadRequest) => {
        dispatch(uploadReadingAudioRequest(request));
    };

    const fetchSessionAttempts = (sessionId: string) => {
        dispatch(fetchSessionAttemptsRequest(sessionId));
    };

    // === COMPREHENSION MANAGEMENT ===
    const submitComprehension = (request: SubmitComprehensionRequest) => {
        dispatch(submitComprehensionRequest(request));
    };

    const fetchSessionComprehension = (sessionId: string) => {
        dispatch(fetchSessionComprehensionRequest(sessionId));
    };

    // === FEEDBACK MANAGEMENT ===
    const fetchSessionFeedback = (sessionId: string) => {
        dispatch(fetchSessionFeedbackRequest(sessionId));
    };

    const generateSessionFeedback = (sessionId: string) => {
        dispatch(generateSessionFeedbackRequest(sessionId));
    };

    const setFeedbackTone = (tone: FeedbackTone) => {
        dispatch(setCurrentFeedbackTone(tone));
    };

    // === ANALYTICS MANAGEMENT ===
    const fetchAnalytics = (days?: number) => {
        dispatch(fetchReadingAnalyticsRequest(days || 30));
    };

    const fetchWeeklyProgress = (weeks?: number) => {
        dispatch(fetchWeeklyProgressRequest(weeks || 12));
    };

    const fetchMonthlyProgress = (months?: number) => {
        dispatch(fetchMonthlyProgressRequest(months || 6));
    };

    const fetchSkillBreakdown = () => {
        dispatch(fetchSkillBreakdownRequest());
    };

    const fetchProgressTimeline = (days?: number) => {
        dispatch(fetchProgressTimelineRequest(days || 30));
    };

    const fetchStreakData = () => {
        dispatch(fetchStreakDataRequest());
    };

    const fetchSkillTrends = () => {
        dispatch(fetchSkillTrendsRequest());
    };

    // === WAVEFORM & AUDIO ANALYSIS ===
    const fetchWaveformData = (audioUrl: string) => {
        dispatch(fetchWaveformDataRequest(audioUrl));
    };

    const compareAudio = (userAudioUrl: string, referenceAudioUrl?: string, targetSentence?: string) => {
        dispatch(compareReadingAudioRequest({ userAudioUrl, referenceAudioUrl, targetSentence }));
    };

    // === OPTIMIZED COMPUTED STATE ===
    const isLoading = Object.values(loadingStates).some(Boolean)

    const hasError = Object.values(errorStates).some(Boolean)

    const currentProgress = {
        currentSentence: currentSession?.current_sentence,
        totalSentences: currentSession?.total_sentences,
        progressPercentage: currentSession?.reading_progress_percentage,
        isCompleted: currentSession?.is_completed,
    }

    // === RETURN HOOK INTERFACE ===
    return {
        // State
        readingState,
        sessionsState,
        analyticsState,

        // Computed values
        isLoading,
        hasError,
        currentProgress,

        // Reading passages
        fetchReadingPassages,
        fetchReadingPassageById,
        fetchPassageQuestions,
        setFilters,
        clearFilters,

        // Recommendations
        fetchRecommendations,
        rateRecommendation,

        // User profile
        fetchUserProfile,
        updateUserProfile,

        // Sessions
        fetchReadingSessions,
        fetchReadingSessionById,
        startReadingSession,
        completeReadingSession,

        // Audio
        uploadAudio,
        fetchSessionAttempts,

        // Comprehension
        submitComprehension,
        fetchSessionComprehension,

        // Feedback
        fetchSessionFeedback,
        generateSessionFeedback,
        setFeedbackTone,

        // Analytics
        fetchAnalytics,
        fetchWeeklyProgress,
        fetchMonthlyProgress,
        fetchSkillBreakdown,
        fetchProgressTimeline,
        fetchStreakData,
        fetchSkillTrends,

        // Audio analysis
        fetchWaveformData,
        compareAudio,
    };
}; 